import { getDatabase, ref, get, set, push, update, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    WorkOrder,
    WorkOrderWithBuilding,
    WorkOrderStatus,
    WorkOrderPriority,
    CompletionData,
    getComponentKeys,
    getAssignees,
    getDelegations
} from '../types/workOrders';
import { ProjectNote, ProjectChecklist, ChecklistItem } from '../types/projects';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { Attachment } from '../types/attachments';
import {
    uploadWorkOrderAttachment,
    deleteWorkOrderAttachment,
    getWorkOrderAttachmentDownloadUrl,
    getWorkOrderDbPath
} from '../Helpers/WorkOrderAttachmentUtils';
import {
    createPhotoModelFromFile,
    uploadPhotoToDatabaseAndStorage
} from '../Helpers/ImageUploadUtils';
import { PhotoModel } from '../types/protocols';
import { analyticsApi } from './analyticsApi';
import { guestTokensApi } from './guestTokensApi';
import type { CreateGuestTokenPayload, CreateGuestTokenResponse, GuestLinkValidityPayload } from '../types/guestAccess';
import { softDeleteFile, DeletedFileTombstone } from './fileDeletionApi';
import { recordActivity } from './activityApi';

/**
 * Granular section identifiers for a work order's enabledSections toggles.
 */
export type WorkOrderSection =
    | 'notes'
    | 'checklists'
    | 'beforePictures'
    | 'progressPictures'
    | 'afterPictures';

/**
 * Photo bucket types for granular work-order photos (KTD3).
 */
export type WorkOrderPhotoType = 'beforePictures' | 'progressPictures' | 'afterPictures';

/**
 * What happens to the archived issue a work order was converted from when the
 * work order is deleted: back to the active issues, or left in the archive.
 */
export type SourceIssueAction = 'restore' | 'keep';

export interface WorkOrderPhotoUploadResult {
    success: boolean;
    photoModel?: PhotoModel;
    error?: string;
}

export class WorkOrdersApi {
    private db = getDatabase();

    private getFunctionsInstance() {
        return getFunctions(undefined, 'europe-north1');
    }

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }
        return user;
    }

    /**
     * Verify that the current user is an admin
     */
    private async verifyAdmin(): Promise<void> {
        const user = this.getCurrentUser();
        const adminRef = ref(this.db, `users/userList/${user.uid}/admin`);
        const adminSnapshot = await get(adminRef);
        if (!adminSnapshot.exists() || adminSnapshot.val() !== true) {
            throw new Error('Du måste vara administratör för att utföra denna åtgärd');
        }
    }

    /**
     * Validate and parse work order data from Firebase
     * Returns null if required fields are missing
     */
    private parseWorkOrderData(
        data: unknown,
        key: string,
        buildingName?: string
    ): WorkOrderWithBuilding | null {
        if (!data || typeof data !== 'object') {
            return null;
        }

        const d = data as Record<string, unknown>;

        // Required fields
        if (
            typeof d.title !== 'string' ||
            typeof d.status !== 'string' ||
            typeof d.buildingKey !== 'string' ||
            typeof d.priority !== 'string'
        ) {
            console.warn(`Invalid work order data at ${key}: missing required fields`);
            return null;
        }

        // Validate status is a valid enum value
        if (!Object.values(WorkOrderStatus).includes(d.status as WorkOrderStatus)) {
            console.warn(`Invalid work order status at ${key}: ${d.status}`);
            return null;
        }

        // Validate priority is a valid enum value
        if (!Object.values(WorkOrderPriority).includes(d.priority as WorkOrderPriority)) {
            console.warn(`Invalid work order priority at ${key}: ${d.priority}`);
            return null;
        }

        return {
            key,
            buildingName: buildingName ?? 'Okänd byggnad',
            ...d
        } as WorkOrderWithBuilding;
    }

    // ============ READ OPERATIONS ============

    /**
     * Get all active work orders across all buildings
     */
    async getAll(): Promise<WorkOrderWithBuilding[]> {
        // Fetch work orders and building summaries in parallel
        const [workOrdersSnapshot, buildingsSnapshot] = await Promise.all([
            get(ref(this.db, 'workOrders/active')),
            get(ref(this.db, 'buildingsSummary'))
        ]);

        if (!workOrdersSnapshot.exists()) {
            return [];
        }

        // Map of buildingKey -> buildingName for the buildings that exist.
        // removeBuilding archives only the building, so work orders of a removed
        // building stay in workOrders/active; they are treated as non-existent here.
        const buildingNames = new Map<string, string | undefined>();
        if (buildingsSnapshot.exists()) {
            buildingsSnapshot.forEach((child) => {
                buildingNames.set(child.key!, child.val()?.name);
            });
        }

        const workOrders: WorkOrderWithBuilding[] = [];
        workOrdersSnapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            const buildingKey = data?.buildingKey;
            if (typeof buildingKey !== 'string' || !buildingNames.has(buildingKey)) return;
            const workOrder = this.parseWorkOrderData(data, key, buildingNames.get(buildingKey));

            if (workOrder) {
                workOrders.push(workOrder);
            }
        });

        return workOrders;
    }

    /**
     * Get work orders for a specific building
     */
    async getByBuilding(buildingKey: string): Promise<WorkOrder[]> {
        const snapshot = await get(ref(this.db, 'workOrders/active'));

        if (!snapshot.exists()) {
            return [];
        }

        const workOrders: WorkOrder[] = [];
        snapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            if (data?.buildingKey === buildingKey) {
                const workOrder = this.parseWorkOrderData(data, key);
                if (workOrder) {
                    workOrders.push(workOrder);
                }
            }
        });

        return workOrders;
    }

    /**
     * Get archived work orders for a building (flat archive structure)
     */
    async getArchivedByBuilding(buildingKey: string): Promise<WorkOrder[]> {
        const snapshot = await get(ref(this.db, 'workOrders/archive'));

        if (!snapshot.exists()) {
            return [];
        }

        const workOrders: WorkOrder[] = [];
        snapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            if (data?.buildingKey === buildingKey) {
                const workOrder = this.parseWorkOrderData(data, key);
                if (workOrder) {
                    workOrders.push(workOrder);
                }
            }
        });

        return workOrders;
    }

    /**
     * Get a single work order by key
     * Checks active first, then searches archive if not found
     */
    async getById(workOrderKey: string): Promise<WorkOrderWithBuilding | null> {
        const [workOrderSnapshot, buildingsSnapshot] = await Promise.all([
            get(ref(this.db, `workOrders/active/${workOrderKey}`)),
            get(ref(this.db, 'buildingsSummary'))
        ]);

        let data = workOrderSnapshot.exists() ? workOrderSnapshot.val() : null;

        // If not found in active, check archive (flat structure)
        if (!data) {
            const archiveSnapshot = await get(ref(this.db, `workOrders/archive/${workOrderKey}`));
            if (archiveSnapshot.exists()) {
                data = archiveSnapshot.val();
            }
        }

        if (!data) {
            return null;
        }

        // Get building name
        let buildingName: string | undefined;
        if (data?.buildingKey && buildingsSnapshot.exists()) {
            const buildingSummary = buildingsSnapshot.child(data.buildingKey).val();
            buildingName = buildingSummary?.name;
        }

        return this.parseWorkOrderData(data, workOrderKey, buildingName);
    }

    /**
     * Get all archived work orders
     */
    async getArchive(): Promise<WorkOrderWithBuilding[]> {
        const [archiveSnapshot, buildingsSnapshot] = await Promise.all([
            get(ref(this.db, 'workOrders/archive')),
            get(ref(this.db, 'buildingsSummary'))
        ]);

        if (!archiveSnapshot.exists()) {
            return [];
        }

        // Build map of buildingKey -> buildingName
        const buildingNames: Record<string, string> = {};
        if (buildingsSnapshot.exists()) {
            buildingsSnapshot.forEach((child) => {
                const summary = child.val();
                if (summary?.name) {
                    buildingNames[child.key!] = summary.name;
                }
            });
        }

        const workOrders: WorkOrderWithBuilding[] = [];
        archiveSnapshot.forEach((child) => {
            const key = child.key;
            if (!key) return;

            const data = child.val();
            const buildingName = data?.buildingKey ? buildingNames[data.buildingKey] : undefined;
            const workOrder = this.parseWorkOrderData(data, key, buildingName);

            if (workOrder) {
                workOrders.push(workOrder);
            }
        });

        return workOrders;
    }

    /**
     * Get archived work orders for a specific year
     * Client-side filter by archivedYear field
     */
    async getArchiveByYear(year: number): Promise<WorkOrderWithBuilding[]> {
        const allArchived = await this.getArchive();
        return allArchived.filter(wo => wo.archivedYear === year);
    }

    // ============ ADMIN MUTATIONS ============

    /**
     * Create a new work order (admin only)
     */
    async create(data: Omit<WorkOrder, 'key' | 'createdBy' | 'createdAt' | 'status'>): Promise<string> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        if (!data.title?.trim()) {
            throw new Error('Titel krävs');
        }

        if (data.title.trim().length > 200) {
            throw new Error('Titeln får vara max 200 tecken');
        }

        if (!data.buildingKey) {
            throw new Error('Byggnad krävs');
        }

        // Normalize linked components (accepts componentKeys and/or legacy componentKey)
        const componentKeys = getComponentKeys(data);

        // Validate that requiresConditionUpdate is only true if a component is linked
        if (data.requiresConditionUpdate && componentKeys.length === 0) {
            throw new Error('Skicka inte villkorsuppdatering utan komponent');
        }

        const workOrdersRef = ref(this.db, 'workOrders/active');
        const newRef = push(workOrdersRef);
        const key = newRef.key!;

        const createdAt = formatDateForStorage(new Date());

        const workOrder: WorkOrder = {
            key,
            buildingKey: data.buildingKey,
            title: data.title.trim(),
            description: data.description?.trim() || undefined,
            priority: data.priority || WorkOrderPriority.MEDIUM,
            dueDate: data.dueDate || undefined,
            status: WorkOrderStatus.OPEN,
            componentKeys: componentKeys.length > 0 ? componentKeys : undefined,
            projectKey: data.projectKey || undefined,
            unitKey: data.unitKey || undefined,
            unitLabel: data.unitKey ? data.unitLabel || undefined : undefined,
            // Multi-assignee shape: seed the assignees map when created with an
            // internal assignee, never the legacy scalar. The assignment e-mail is
            // opt-in (sendAssignmentEmail); the trigger consumes the flag.
            assignees: data.assignedTo
                ? { [data.assignedTo]: { assignedBy: user.uid, assignedAt: createdAt } }
                : undefined,
            sendAssignmentEmail: data.assignedTo && data.sendAssignmentEmail ? true : undefined,
            requiresPhoto: data.requiresPhoto ?? false,
            requiresComment: data.requiresComment ?? false,
            requiresConditionUpdate: data.requiresConditionUpdate ?? false,
            enabledSections: data.enabledSections || undefined,
            createdBy: user.uid,
            createdAt,
            maintenanceTemplateId: data.maintenanceTemplateId || undefined,
            // Pin the source activity's component immutably so the reschedule
            // trigger is unaffected by later edits to componentKeys. Never touched
            // by update()/updateComponents().
            maintenanceComponentKey: data.maintenanceComponentKey || undefined
        };

        // Remove undefined values for Firebase
        const cleanWorkOrder = Object.fromEntries(
            Object.entries(workOrder).filter(([_, v]) => v !== undefined)
        );

        await set(newRef, cleanWorkOrder);

        analyticsApi.trackWorkOrderCreated(data.buildingKey, data.priority || WorkOrderPriority.MEDIUM);

        recordActivity('workOrder.created', { buildingKey: data.buildingKey, targetKey: key, label: workOrder.title });
        return key;
    }

    /**
     * Update work order details (admin only)
     */
    async update(workOrderKey: string, data: Partial<WorkOrder>): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        // Don't allow updating key or audit fields via this method
        const { key, createdBy, createdAt, completedBy, completedAt, ...allowedUpdates } = data;

        // Validate that requiresConditionUpdate is only true if a component is linked
        if (allowedUpdates.requiresConditionUpdate && getComponentKeys(allowedUpdates).length === 0) {
            // Check if existing work order has linked components
            const existing = await this.getById(workOrderKey);
            if (!existing || getComponentKeys(existing).length === 0) {
                throw new Error('Villkorsuppdatering kräver länkad komponent');
            }
        }

        const updates: Record<string, unknown> = {
            ...allowedUpdates,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        };

        // Clean undefined values
        Object.keys(updates).forEach(key => {
            if (updates[key] === undefined) {
                delete updates[key];
            }
        });

        // Normalize component links when they change (null clears the field in
        // RTDB, undefined would be stripped above). Clearing all links also
        // clears requiresConditionUpdate unless the caller set it explicitly,
        // keeping the "condition update requires a linked component" invariant.
        if (data.componentKeys !== undefined) {
            const componentKeys = getComponentKeys(data);
            updates.componentKeys = componentKeys.length > 0 ? componentKeys : null;
            if (componentKeys.length === 0 && data.requiresConditionUpdate === undefined) {
                updates.requiresConditionUpdate = false;
            }
        }

        // Unit link (phase 3): an explicit key on the input replaces the link; '' clears it with its label.
        // `undefined` (the form's empty picker) leaves the link alone so unrelated edits never drop it.
        if (data.unitKey !== undefined) {
            updates.unitKey = data.unitKey || null;
            updates.unitLabel = data.unitKey ? data.unitLabel || null : null;
        }

        await update(ref(this.db, `workOrders/active/${workOrderKey}`), updates);
        recordActivity('workOrder.updated', { buildingKey: data.buildingKey, targetKey: workOrderKey, label: data.title?.trim() });
    }

    /**
     * Replace the linked components of an open work order (admin only).
     * Pass an empty array to clear all links, which also clears
     * requiresConditionUpdate to keep the "condition update requires a linked
     * component" invariant.
     */
    async updateComponents(workOrderKey: string, componentKeys: string[]): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        const workOrderRef = ref(this.db, `workOrders/active/${workOrderKey}`);
        const snapshot = await get(workOrderRef);

        if (!snapshot.exists()) {
            throw new Error('Ärendet hittades inte');
        }

        const normalizedKeys = getComponentKeys({ componentKeys });

        const updates: Record<string, unknown> = {
            componentKeys: normalizedKeys.length > 0 ? normalizedKeys : null,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        };

        // No components → condition update is no longer valid.
        if (normalizedKeys.length === 0) {
            updates.requiresConditionUpdate = false;
        }

        await update(workOrderRef, updates);
        recordActivity('workOrder.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: workOrderKey, label: snapshot.val()?.title });
    }

    /**
     * Update the title of an open work order (admin only).
     *
     * The title is required — an empty/whitespace value is rejected. Only open
     * work orders live under workOrders/active.
     */
    async updateTitle(workOrderKey: string, title: string): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        const trimmed = title.trim();
        if (!trimmed) {
            throw new Error('Titel är obligatorisk');
        }

        const workOrderRef = ref(this.db, `workOrders/active/${workOrderKey}`);
        const snapshot = await get(workOrderRef);

        if (!snapshot.exists()) {
            throw new Error('Ärendet hittades inte');
        }

        await update(workOrderRef, {
            title: trimmed,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('workOrder.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: workOrderKey, label: trimmed });
    }

    /**
     * Update the description of an open work order (admin only).
     *
     * Pass an empty/whitespace string to clear the description. Only open work
     * orders live under workOrders/active.
     */
    async updateDescription(workOrderKey: string, description: string): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        const workOrderRef = ref(this.db, `workOrders/active/${workOrderKey}`);
        const snapshot = await get(workOrderRef);

        if (!snapshot.exists()) {
            throw new Error('Ärendet hittades inte');
        }

        await update(workOrderRef, {
            description: description.trim() || null,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('workOrder.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: workOrderKey, label: snapshot.val()?.title });
    }

    /**
     * Update the due date of an open work order (admin only).
     *
     * Accepts a date-only string (YYYY-MM-DD) and stores it end-of-day in
     * Stockholm time (`YYYY-MM-DD 23:59:59`), matching how creation sets it.
     * Pass null to clear the due date. Only open work orders live under
     * workOrders/active.
     */
    async updateDueDate(workOrderKey: string, dueDate: string | null): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        const workOrderRef = ref(this.db, `workOrders/active/${workOrderKey}`);
        const snapshot = await get(workOrderRef);

        if (!snapshot.exists()) {
            throw new Error('Ärendet hittades inte');
        }

        await update(workOrderRef, {
            dueDate: dueDate ? `${dueDate} 23:59:59` : null,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('workOrder.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: workOrderKey, label: snapshot.val()?.title });
    }

    /**
     * Update the priority of an open work order (admin only). Only open work
     * orders live under workOrders/active.
     */
    async updatePriority(workOrderKey: string, priority: WorkOrderPriority): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        const workOrderRef = ref(this.db, `workOrders/active/${workOrderKey}`);
        const snapshot = await get(workOrderRef);

        if (!snapshot.exists()) {
            throw new Error('Ärendet hittades inte');
        }

        await update(workOrderRef, {
            priority,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('workOrder.updated', { buildingKey: snapshot.val()?.buildingKey, targetKey: workOrderKey, label: snapshot.val()?.title });
    }

    /**
     * Add a single internal assignee without disturbing existing assignees or
     * delegates (admin only). Migrates a legacy scalar assignedTo into the map.
     */
    async addAssignee(workOrderKey: string, userId: string, sendEmail: boolean = true): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();
        const now = formatDateForStorage(new Date());

        const snapshot = await get(ref(this.db, `workOrders/active/${workOrderKey}`));
        const workOrder = snapshot.val() as WorkOrder | null;
        if (!workOrder) {
            throw new Error('Ärendet hittades inte');
        }

        const updates: Record<string, unknown> = {
            [`workOrders/active/${workOrderKey}/updatedBy`]: user.uid,
            [`workOrders/active/${workOrderKey}/updatedAt`]: now,
        };

        // Migrate a legacy scalar assignedTo into the map (so the shim never
        // double-counts) and drop it.
        if (workOrder.assignedTo) {
            const legacy = workOrder.assignedTo;
            if (legacy !== userId) {
                updates[`workOrders/active/${workOrderKey}/assignees/${legacy}`] = {
                    assignedBy: '',
                    assignedAt: '',
                };
            }
            updates[`workOrders/active/${workOrderKey}/assignedTo`] = null;
        }

        updates[`workOrders/active/${workOrderKey}/assignees/${userId}`] = {
            assignedBy: user.uid,
            assignedAt: now,
        };
        // Opt-in email for the newly-added assignee (consumed + cleared by the
        // workOrderAssignmentNotification trigger).
        updates[`workOrders/active/${workOrderKey}/sendAssignmentEmail`] = sendEmail ? true : null;

        await update(ref(this.db), updates);
        recordActivity('workOrder.updated', { buildingKey: workOrder.buildingKey, targetKey: workOrderKey, label: workOrder.title });
    }

    /**
     * Remove a single internal assignee (admin only). Delegates and other
     * assignees are untouched.
     */
    async removeAssignee(workOrderKey: string, userId: string): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();
        const now = formatDateForStorage(new Date());

        const snapshot = await get(ref(this.db, `workOrders/active/${workOrderKey}`));
        const workOrder = snapshot.val() as WorkOrder | null;
        if (!workOrder) {
            throw new Error('Ärendet hittades inte');
        }

        const updates: Record<string, unknown> = {
            [`workOrders/active/${workOrderKey}/assignees/${userId}`]: null,
            [`workOrders/active/${workOrderKey}/updatedBy`]: user.uid,
            [`workOrders/active/${workOrderKey}/updatedAt`]: now,
        };
        // Clear a legacy scalar still pointing at this user.
        if (workOrder.assignedTo === userId) {
            updates[`workOrders/active/${workOrderKey}/assignedTo`] = null;
        }

        await update(ref(this.db), updates);
        recordActivity('workOrder.updated', { buildingKey: workOrder.buildingKey, targetKey: workOrderKey, label: workOrder.title });
    }

    /**
     * Replace the full set of internal assignees (admin only). Used by the
     * single-select compat path and multi-select UI. Delegates untouched.
     */
    async setAssignees(workOrderKey: string, userIds: string[], sendEmail: boolean = true): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();
        const now = formatDateForStorage(new Date());

        const snapshot = await get(ref(this.db, `workOrders/active/${workOrderKey}`));
        const workOrder = snapshot.val() as WorkOrder | null;
        if (!workOrder) {
            throw new Error('Ärendet hittades inte');
        }

        // Preserve assignedBy/assignedAt for assignees that already exist; new
        // ones get fresh metadata.
        const existing = getAssignees(workOrder);
        const nextAssignees: Record<string, { assignedBy: string; assignedAt: string }> = {};
        for (const uid of userIds) {
            nextAssignees[uid] = existing[uid] || { assignedBy: user.uid, assignedAt: now };
        }

        const anyAdded = userIds.some((uid) => !existing[uid]);

        const updates: Record<string, unknown> = {
            [`workOrders/active/${workOrderKey}/assignees`]: userIds.length > 0 ? nextAssignees : null,
            // Drop any legacy scalar — the map is now authoritative.
            [`workOrders/active/${workOrderKey}/assignedTo`]: null,
            [`workOrders/active/${workOrderKey}/updatedBy`]: user.uid,
            [`workOrders/active/${workOrderKey}/updatedAt`]: now,
            [`workOrders/active/${workOrderKey}/sendAssignmentEmail`]: anyAdded && sendEmail ? true : null,
        };

        await update(ref(this.db), updates);
        recordActivity('workOrder.updated', { buildingKey: workOrder.buildingKey, targetKey: workOrderKey, label: workOrder.title });
    }

    /**
     * Delegate the work order to an external user (admin only)
     * Writes the delegation, ensures the user has a live personal link (minted
     * with `linkValidity` when none) and e-mails the deep link. Optional
     * emailOptions let the admin add a custom message, override the Reply-To,
     * and BCC themselves. `reminders` is the composer's reminder choice
     * (null = none); left out, the callable applies the instance default.
     */
    async assignToExternal(
        workOrderKey: string,
        buildingKey: string,
        externalUserId: string,
        emailOptions?: { subject?: string; customMessage?: string; replyTo?: string; sendCopyToAdmin?: boolean },
        linkValidity: GuestLinkValidityPayload = {},
        reminders?: CreateGuestTokenPayload['reminders']
    ): Promise<CreateGuestTokenResponse> {
        await this.verifyAdmin();

        return guestTokensApi.create({
            taskType: 'workOrder',
            taskKey: workOrderKey,
            buildingKey,
            externalUserId,
            sendEmail: true,
            ...emailOptions,
            ...linkValidity,
            ...(reminders !== undefined ? { reminders } : {}),
        });
    }

    /**
     * Link or unlink a work order to a project (admin only)
     * Works for both active and archived work orders
     * Validates that project belongs to same building as work order
     *
     * `options.dueDate` (YYYY-MM-DD, or null to clear) rewrites the due date in
     * the same atomic write — used when a delmoment is carried over to a new
     * year's project and its old due date no longer applies. Leave undefined
     * to keep the current due date. Only open work orders have a due date, so
     * it is rejected for archived ones.
     */
    async linkToProject(
        workOrderKey: string,
        projectKey: string | null,
        options: { dueDate?: string | null } = {}
    ): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        // Check if work order is active or archived
        const activeSnapshot = await get(ref(this.db, `workOrders/active/${workOrderKey}`));
        let path: string;
        let workOrderData: Record<string, unknown> | null = null;

        if (activeSnapshot.exists()) {
            path = `workOrders/active/${workOrderKey}`;
            workOrderData = activeSnapshot.val();
        } else {
            // Check archive
            const archiveSnapshot = await get(ref(this.db, `workOrders/archive/${workOrderKey}`));
            if (archiveSnapshot.exists()) {
                path = `workOrders/archive/${workOrderKey}`;
                workOrderData = archiveSnapshot.val();
            } else {
                throw new Error('Ärendet hittades inte');
            }
        }

        // Validate project belongs to same building
        if (projectKey && workOrderData) {
            const workOrderBuildingKey = workOrderData.buildingKey as string;
            const projectSnapshot = await get(ref(this.db, `projects/${projectKey}`));
            if (!projectSnapshot.exists()) {
                throw new Error('Projektet hittades inte');
            }
            const projectBuildingKey = projectSnapshot.val()?.buildingKey as string;
            if (projectBuildingKey !== workOrderBuildingKey) {
                throw new Error('Projektet tillhör inte samma byggnad som ärendet');
            }
        }

        const updates: Record<string, unknown> = {
            projectKey: projectKey,
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        };

        if (options.dueDate !== undefined) {
            if (!activeSnapshot.exists()) {
                throw new Error('Förfallodatum kan bara ändras på öppna ärenden');
            }
            // Same end-of-day stamp as updateDueDate and creation
            updates.dueDate = options.dueDate ? `${options.dueDate} 23:59:59` : null;
        }

        await update(ref(this.db, path), updates);
        recordActivity('workOrder.updated', {
            buildingKey: workOrderData?.buildingKey as string | undefined,
            targetKey: workOrderKey,
            label: workOrderData?.title as string | undefined
        });
    }

    /**
     * Undelegate one external user from a work order (admin only).
     *
     * Routes through the guestTokenApi callable, which removes the delegation
     * and its `externalDelegations` mirror entry in one update. Other delegates,
     * internal assignees and the user's personal link are left untouched.
     */
    async revokeDelegation(workOrderKey: string, externalUserId: string): Promise<void> {
        await this.verifyAdmin();
        await guestTokensApi.revoke({ taskType: 'workOrder', taskKey: workOrderKey, externalUserId });
    }

    /**
     * Cancel a work order and move to archive (admin only)
     */
    async cancel(workOrderKey: string): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        // Get the work order first
        const workOrder = await this.getById(workOrderKey);
        if (!workOrder) {
            throw new Error('Ärende hittades inte');
        }

        if (workOrder.status !== WorkOrderStatus.OPEN) {
            throw new Error('Kan endast avbryta öppna ärenden');
        }

        const now = formatDateForStorage(new Date());
        const archivedYear = new Date().getFullYear();

        // Update status and move to archive atomically (flat structure)
        const updatedWorkOrder = {
            ...workOrder,
            status: WorkOrderStatus.CANCELLED,
            updatedBy: user.uid,
            updatedAt: now,
            archivedYear
        };

        // Remove the buildingName property as it's not stored in DB
        delete (updatedWorkOrder as any).buildingName;

        // Delegations stay on the archived record; a cancelled work order simply
        // vanishes from the delegates' portal.
        const updates: Record<string, unknown> = {
            [`workOrders/active/${workOrderKey}`]: null,
            [`workOrders/archive/${workOrderKey}`]: updatedWorkOrder
        };

        await update(ref(this.db), updates);
        recordActivity('workOrder.archived', { buildingKey: workOrder.buildingKey, targetKey: workOrderKey, label: workOrder.title });
    }

    /**
     * Permanently delete a work order (admin only).
     *
     * Allowed for open and cancelled work orders, active or archived. A
     * completed work order is history (condition updates, cost, maintenance
     * reschedule) and can only be deleted when its building is gone — an
     * orphan that can neither be completed meaningfully nor archived cleanly.
     *
     * The DB record is removed outright, but every file it referenced (source
     * photos, before/progress/after pictures, legacy completion photos and
     * attachments) gets a `deletedFiles/{fileKey}` tombstone so the future
     * Storage cleanup job can purge the blobs — the same soft-delete contract
     * as single-file deletes. Every delegate's `externalDelegations` mirror
     * entry is removed too, so the work order leaves their portal.
     *
     * A work order converted from an issue shares its `photos` with the
     * archived issue (same storage paths), so those are NOT tombstoned while
     * the issue still exists. `sourceIssue` decides what happens to that
     * issue: 'restore' moves it back to issues/active as an ordinary open
     * issue, 'keep' (default) leaves it archived and replaces its now dangling
     * work-order link with an archive reason. A maintenance activity needs no
     * handling: it is hidden only while an open work order exists.
     *
     * Everything happens in ONE atomic multi-path update.
     */
    async delete(
        workOrderKey: string,
        options: { sourceIssue?: SourceIssueAction } = {}
    ): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        const workOrderPath = await getWorkOrderDbPath(workOrderKey);
        if (!workOrderPath) {
            throw new Error('Ärendet hittades inte');
        }

        const snapshot = await get(ref(this.db, workOrderPath));
        if (!snapshot.exists()) {
            throw new Error('Ärendet hittades inte');
        }
        const raw = snapshot.val() as WorkOrder;
        const deletedAt = formatDateForStorage(new Date());

        if (raw.status === WorkOrderStatus.COMPLETED) {
            const buildingSnapshot = await get(ref(this.db, `buildingsSummary/${raw.buildingKey}`));
            if (buildingSnapshot.exists()) {
                throw new Error('Slutförda ärenden kan inte tas bort');
            }
        }

        const updates: Record<string, unknown> = {
            [workOrderPath]: null
        };

        // Source issue: restore it or leave it archived, and work out whether
        // it still references the photos this work order carries.
        let issueHoldsPhotos = false;
        if (raw.sourceIssueKey) {
            const archivePath = `issues/archive/${raw.sourceIssueKey}`;
            const [archivedSnapshot, activeSnapshot] = await Promise.all([
                get(ref(this.db, archivePath)),
                get(ref(this.db, `issues/active/${raw.sourceIssueKey}`))
            ]);
            issueHoldsPhotos = archivedSnapshot.exists() || activeSnapshot.exists();

            if (options.sourceIssue === 'restore') {
                if (!archivedSnapshot.exists()) {
                    throw new Error('Felanmälan hittades inte i arkivet och kan inte återställas');
                }
                const {
                    convertedToWorkOrderKey,
                    archivedAt,
                    archivedBy,
                    archiveReason,
                    // Rules never let a client create it on an active issue; the restored issue is open (and pending) again
                    costAssessment,
                    ...restoredIssue
                } = archivedSnapshot.val() as Record<string, unknown>;
                updates[archivePath] = null;
                updates[`issues/active/${raw.sourceIssueKey}`] = restoredIssue;
            } else if (
                archivedSnapshot.exists() &&
                archivedSnapshot.val()?.convertedToWorkOrderKey === workOrderKey
            ) {
                updates[`${archivePath}/convertedToWorkOrderKey`] = null;
                updates[`${archivePath}/archiveReason`] = 'Ärendet togs bort';
            }
        }

        const tombstoneFiles = (
            container: string,
            entityType: 'workOrderPhoto' | 'workOrderAttachment',
            files: Record<string, { storagePath?: string }> | undefined
        ) => {
            if (!files) return;
            for (const [fileKey, metadata] of Object.entries(files)) {
                const tombstone: DeletedFileTombstone = {
                    originalPath: `${workOrderPath}/${container}/${fileKey}`,
                    entityType,
                    storagePath: typeof metadata?.storagePath === 'string' ? metadata.storagePath : '',
                    metadata: metadata ?? {},
                    deletedBy: user.uid,
                    deletedAt
                };
                updates[`deletedFiles/${fileKey}`] = tombstone;
            }
        };
        if (!issueHoldsPhotos) {
            tombstoneFiles('photos', 'workOrderPhoto', raw.photos);
        }
        // Legacy pre-afterPictures field: tombstone its blobs if an old record still carries them.
        tombstoneFiles('completionPhotos', 'workOrderPhoto', raw.completionPhotos);
        tombstoneFiles('beforePictures', 'workOrderPhoto', raw.beforePictures);
        tombstoneFiles('progressPictures', 'workOrderPhoto', raw.progressPictures);
        tombstoneFiles('afterPictures', 'workOrderPhoto', raw.afterPictures);
        tombstoneFiles('attachments', 'workOrderAttachment', raw.attachments);

        for (const externalUserId of Object.keys(getDelegations(raw))) {
            updates[`externalDelegations/${externalUserId}/${workOrderKey}`] = null;
        }

        await update(ref(this.db), updates);
        recordActivity('workOrder.deleted', { buildingKey: raw.buildingKey, targetKey: workOrderKey, label: raw.title });
    }

    /**
     * Re-issue an archived work order (admin only).
     *
     * Moves a completed or cancelled work order from the archive back to active
     * with status OPEN, stripping the completion/archival metadata so it behaves
     * like a fresh open work order. Used when a contractor marked a work order
     * done prematurely and it needs to be redone.
     *
     * Keeps the SAME key as the original, which matters for maintenance: the
     * rescheduleMaintenanceOnCompletion trigger keys its idempotency guard on the
     * work-order key (lastCompletedWorkOrderKey), so re-completing a reissued
     * maintenance work order will NOT advance the schedule a second time — the
     * premature completion already advanced it once.
     *
     * Intentionally NOT reverted here:
     * - Condition updates written to components at completion stay applied; a
     *   re-completion overwrites them, and the change log keeps the history.
     * - External delegations are cleared (with their `externalDelegations`
     *   mirror entries) so the admin decides afresh who gets the redo; delegate
     *   again via assignToExternal.
     */
    async reissue(workOrderKey: string): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        // Must currently live in the archive (flat structure).
        const archiveSnapshot = await get(ref(this.db, `workOrders/archive/${workOrderKey}`));
        if (!archiveSnapshot.exists()) {
            throw new Error('Ärendet hittades inte i arkivet');
        }
        const workOrder = archiveSnapshot.val() as Record<string, unknown>;

        if (
            workOrder.status !== WorkOrderStatus.COMPLETED &&
            workOrder.status !== WorkOrderStatus.CANCELLED
        ) {
            throw new Error('Endast arkiverade ärenden kan återutfärdas');
        }

        const now = formatDateForStorage(new Date());

        // Rebuild as an open, active work order. Null-valued keys are removed by
        // RTDB when the object is written, which strips the completion/archival
        // fields cleanly.
        const reopened: Record<string, unknown> = {
            ...workOrder,
            status: WorkOrderStatus.OPEN,
            updatedBy: user.uid,
            updatedAt: now,
            // Completion metadata
            completedBy: null,
            completedAt: null,
            completionComment: null,
            actualCost: null,
            actualCostInvestmentPercentage: null,
            // Archive metadata
            archivedYear: null,
            // Clear the delegation map and legacy scalars so the admin can
            // re-delegate cleanly.
            delegations: null,
            externalUserId: null,
            guestTokenKey: null,
        };

        // Move archive -> active atomically, dropping the delegates' mirror entries.
        const updates: Record<string, unknown> = {
            [`workOrders/archive/${workOrderKey}`]: null,
            [`workOrders/active/${workOrderKey}`]: reopened,
        };
        for (const externalUserId of Object.keys(getDelegations(workOrder as Record<string, unknown>))) {
            updates[`externalDelegations/${externalUserId}/${workOrderKey}`] = null;
        }

        await update(ref(this.db), updates);
        // Reissuing opens the work order anew.
        recordActivity('workOrder.created', {
            buildingKey: workOrder.buildingKey as string | undefined,
            targetKey: workOrderKey,
            label: workOrder.title as string | undefined
        });
    }

    // ============ COMPLETION ============

    /**
     * Complete a work order (via the completeWorkOrder Cloud Function).
     * Used by admins and non-admins alike; the function validates:
     * - User is assigned, work order is unassigned, or user is admin
     * - Required fields (photo, comment) are present
     * - Only completion fields are written
     * - Condition update is applied to components if required
     * - After photos are copied to the component when exactly one is linked
     *
     * `activity` (building and title) only feeds the Användaraktivitet entry.
     */
    async complete(
        workOrderKey: string,
        completionData: CompletionData,
        activity?: { buildingKey?: string; title?: string },
    ): Promise<void> {
        const functions = this.getFunctionsInstance();
        const completeWorkOrder = httpsCallable(functions, 'completeWorkOrder');

        try {
            await completeWorkOrder({
                workOrderKey,
                completionData
            });

            analyticsApi.trackWorkOrderCompleted(workOrderKey);
            recordActivity('workOrder.completed', { buildingKey: activity?.buildingKey, targetKey: workOrderKey, label: activity?.title });
        } catch (error: any) {
            // Re-throw with Swedish message if available
            const message = error.message || 'Kunde inte slutföra ärendet';
            throw new Error(message);
        }
    }


    // ============ SECTION TOGGLES (U2) ============

    /**
     * Base path for an active work order's granular sub-collections.
     */
    private getActiveWorkOrderPath(workOrderKey: string): string {
        return `workOrders/active/${workOrderKey}`;
    }

    /**
     * Enable or disable a single granular section on a work order (admin only).
     * Writes enabledSections/{section} via a plain update — idempotent, no race
     * machinery. Disabling sets the flag false but never deletes the section's
     * data (R3): re-enabling restores it. Available for any work order regardless
     * of projectKey (R2).
     */
    async setSectionEnabled(
        workOrderKey: string,
        section: WorkOrderSection,
        enabled: boolean
    ): Promise<void> {
        await this.verifyAdmin();

        await update(
            ref(this.db, `${this.getActiveWorkOrderPath(workOrderKey)}/enabledSections`),
            { [section]: enabled }
        );
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    /**
     * Update multiple section toggles at once (admin only).
     * Same semantics as setSectionEnabled but batched.
     */
    async updateEnabledSections(
        workOrderKey: string,
        sections: Partial<Record<WorkOrderSection, boolean>>
    ): Promise<void> {
        await this.verifyAdmin();

        await update(
            ref(this.db, `${this.getActiveWorkOrderPath(workOrderKey)}/enabledSections`),
            sections
        );
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    // ============ NOTES (U3) ============

    /**
     * Add a note to a work order
     */
    async addNote(workOrderKey: string, title: string, content: string): Promise<ProjectNote> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }
        if (!content.trim()) {
            throw new Error('Innehåll krävs');
        }

        const user = this.getCurrentUser();
        const notesRef = ref(this.db, `${this.getActiveWorkOrderPath(workOrderKey)}/notes`);
        const newNoteRef = push(notesRef);
        const noteKey = newNoteRef.key!;

        const note: ProjectNote = {
            key: noteKey,
            title: title.trim(),
            content: content.trim(),
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date())
        };

        await set(newNoteRef, note);
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
        return note;
    }

    /**
     * Update a note on a work order
     */
    async updateNote(
        workOrderKey: string,
        noteKey: string,
        title: string,
        content: string
    ): Promise<void> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }
        if (!content.trim()) {
            throw new Error('Innehåll krävs');
        }

        const user = this.getCurrentUser();
        const noteRef = ref(this.db, `${this.getActiveWorkOrderPath(workOrderKey)}/notes/${noteKey}`);

        await update(noteRef, {
            title: title.trim(),
            content: content.trim(),
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    /**
     * Delete a note from a work order
     */
    async deleteNote(workOrderKey: string, noteKey: string): Promise<void> {
        const noteRef = ref(this.db, `${this.getActiveWorkOrderPath(workOrderKey)}/notes/${noteKey}`);
        await remove(noteRef);
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    // ============ CHECKLISTS (U3) ============

    /**
     * Add a checklist to a work order
     */
    async addChecklist(workOrderKey: string, title: string): Promise<ProjectChecklist> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }

        const user = this.getCurrentUser();
        const checklistsRef = ref(this.db, `${this.getActiveWorkOrderPath(workOrderKey)}/checklists`);
        const newChecklistRef = push(checklistsRef);
        const checklistKey = newChecklistRef.key!;

        const checklist: ProjectChecklist = {
            key: checklistKey,
            title: title.trim(),
            items: {},
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date())
        };

        await set(newChecklistRef, checklist);
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
        return checklist;
    }

    /**
     * Update a checklist title on a work order
     */
    async updateChecklistTitle(
        workOrderKey: string,
        checklistKey: string,
        title: string
    ): Promise<void> {
        if (!title.trim()) {
            throw new Error('Titel krävs');
        }

        const checklistRef = ref(
            this.db,
            `${this.getActiveWorkOrderPath(workOrderKey)}/checklists/${checklistKey}`
        );
        await update(checklistRef, { title: title.trim() });
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    /**
     * Delete a checklist from a work order
     */
    async deleteChecklist(workOrderKey: string, checklistKey: string): Promise<void> {
        const checklistRef = ref(
            this.db,
            `${this.getActiveWorkOrderPath(workOrderKey)}/checklists/${checklistKey}`
        );
        await remove(checklistRef);
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    /**
     * Add a checklist item to a work order checklist
     */
    async addChecklistItem(
        workOrderKey: string,
        checklistKey: string,
        text: string
    ): Promise<ChecklistItem> {
        if (!text.trim()) {
            throw new Error('Text krävs');
        }

        const itemsRef = ref(
            this.db,
            `${this.getActiveWorkOrderPath(workOrderKey)}/checklists/${checklistKey}/items`
        );

        // Get existing items to determine next sortOrder
        const existingSnapshot = await get(itemsRef);
        let maxSortOrder = -1;
        if (existingSnapshot.exists()) {
            const items = existingSnapshot.val() as Record<string, ChecklistItem>;
            Object.values(items).forEach(item => {
                const order = item.sortOrder ?? 0;
                if (order > maxSortOrder) {
                    maxSortOrder = order;
                }
            });
        }

        const newItemRef = push(itemsRef);
        const itemKey = newItemRef.key!;

        const item: ChecklistItem = {
            key: itemKey,
            text: text.trim(),
            completed: false,
            createdBy: this.getCurrentUser().uid,
            sortOrder: maxSortOrder + 1
        };

        await set(newItemRef, item);
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
        return item;
    }

    /**
     * Update a checklist item text on a work order
     */
    async updateChecklistItemText(
        workOrderKey: string,
        checklistKey: string,
        itemKey: string,
        text: string
    ): Promise<void> {
        if (!text.trim()) {
            throw new Error('Text krävs');
        }

        const itemRef = ref(
            this.db,
            `${this.getActiveWorkOrderPath(workOrderKey)}/checklists/${checklistKey}/items/${itemKey}`
        );
        await update(itemRef, { text: text.trim() });
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    /**
     * Toggle a checklist item completion on a work order - atomic operation
     */
    async toggleChecklistItem(
        workOrderKey: string,
        checklistKey: string,
        itemKey: string,
        completed: boolean
    ): Promise<void> {
        const user = this.getCurrentUser();
        const basePath = `${this.getActiveWorkOrderPath(workOrderKey)}/checklists/${checklistKey}/items/${itemKey}`;

        if (completed) {
            await update(ref(this.db, basePath), {
                completed: true,
                completedBy: user.uid,
                completedAt: formatDateForStorage(new Date())
            });
        } else {
            // Use multi-path update for atomicity
            const updates: Record<string, boolean | null> = {};
            updates[`${basePath}/completed`] = false;
            updates[`${basePath}/completedBy`] = null;
            updates[`${basePath}/completedAt`] = null;

            await update(ref(this.db), updates);
        }
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    /**
     * Delete a checklist item from a work order
     */
    async deleteChecklistItem(
        workOrderKey: string,
        checklistKey: string,
        itemKey: string
    ): Promise<void> {
        const itemRef = ref(
            this.db,
            `${this.getActiveWorkOrderPath(workOrderKey)}/checklists/${checklistKey}/items/${itemKey}`
        );
        await remove(itemRef);
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    /**
     * Reorder checklist items on a work order - updates sortOrder for all items
     */
    async reorderChecklistItems(
        workOrderKey: string,
        checklistKey: string,
        orderedItemKeys: string[]
    ): Promise<void> {
        const basePath = `${this.getActiveWorkOrderPath(workOrderKey)}/checklists/${checklistKey}/items`;

        // Use multi-path update for atomicity
        const updates: Record<string, number> = {};
        orderedItemKeys.forEach((itemKey, index) => {
            updates[`${basePath}/${itemKey}/sortOrder`] = index;
        });

        await update(ref(this.db), updates);
        recordActivity('workOrder.updated', { targetKey: workOrderKey });
    }

    // ============ GRANULAR PHOTOS (U3, KTD3) ============

    /**
     * Upload a granular photo to a work order (before/progress/after bucket).
     * Database: workOrders/active/{key}/{photoType}/{photoKey}
     * Storage:  workOrders/{buildingKey}/{key}/{photoType}/{photoKey}
     * Path computation is inlined here (KTD7 — no separate WorkOrderPhotoUtils file).
     */
    async uploadWorkOrderPhoto(
        buildingKey: string,
        workOrderKey: string,
        photoType: WorkOrderPhotoType,
        file: File
    ): Promise<WorkOrderPhotoUploadResult> {
        try {
            const user = this.getCurrentUser();

            // Resolve the work order's DB location (active or archive) so admins can
            // add photos to completed/archived work orders, not just active ones.
            const workOrderPath = await getWorkOrderDbPath(workOrderKey);
            if (!workOrderPath) {
                return { success: false, error: 'Ärendet hittades inte' };
            }

            // Generate a unique photo key using Firebase push (flat DB structure)
            const photosRef = ref(this.db, `${workOrderPath}/${photoType}`);
            const newPhotoRef = push(photosRef);
            const photoKey = newPhotoRef.key!;

            // Storage retains buildingKey for organization (KTD3)
            const storagePath = `workOrders/${buildingKey}/${workOrderKey}/${photoType}/${photoKey}`;
            const databasePath = `${workOrderPath}/${photoType}/${photoKey}`;

            // Create PhotoModel (enforces all required fields incl. fileSize)
            const photoModel = await createPhotoModelFromFile(photoKey, storagePath, user.uid, file);

            const uploadResult = await uploadPhotoToDatabaseAndStorage(
                databasePath,
                storagePath,
                photoModel,
                file
            );

            if (!uploadResult.success) {
                return { success: false, error: uploadResult.error };
            }

            recordActivity('photo.uploaded', { buildingKey, targetKey: workOrderKey });

            return { success: true, photoModel };
        } catch (error) {
            console.error('Error uploading work order photo:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Kunde inte ladda upp foto'
            };
        }
    }

    /**
     * Delete a granular photo from a work order (soft delete — tombstones the metadata).
     */
    async deleteWorkOrderPhoto(
        workOrderKey: string,
        photoType: WorkOrderPhotoType,
        photoKey: string
    ): Promise<void> {
        // Resolve active vs archive so admins can manage photos on completed orders.
        const workOrderPath = await getWorkOrderDbPath(workOrderKey);
        if (!workOrderPath) {
            throw new Error('Ärendet hittades inte');
        }
        await softDeleteFile({
            originalPath: `${workOrderPath}/${photoType}/${photoKey}`,
            entityType: 'workOrderPhoto',
        });
    }

    // ============ ATTACHMENTS ============

    /**
     * Upload an attachment to a work order
     * Works for both active and archived work orders
     */
    async uploadAttachment(
        workOrderKey: string,
        file: File,
        displayName?: string
    ): Promise<{ success: boolean; attachment?: Attachment; error?: string }> {
        const user = this.getCurrentUser();

        const result = await uploadWorkOrderAttachment(
            workOrderKey,
            file,
            user.uid,
            displayName
        );

        return result;
    }

    /**
     * Delete an attachment from a work order
     * Works for both active and archived work orders
     */
    async deleteAttachment(
        workOrderKey: string,
        attachmentKey: string
    ): Promise<{ success: boolean; error?: string }> {
        return await deleteWorkOrderAttachment(workOrderKey, attachmentKey);
    }

    /**
     * Change an attachment's display name (active or archived work order).
     * The file and its original `fileName` are untouched; an empty name
     * removes the custom name so readers fall back to `fileName`.
     */
    async renameAttachment(workOrderKey: string, attachmentKey: string, displayName: string | undefined): Promise<void> {
        this.getCurrentUser();
        const workOrderPath = await getWorkOrderDbPath(workOrderKey);
        if (!workOrderPath) throw new Error('Ärendet hittades inte');
        const attachmentPath = `${workOrderPath}/attachments/${attachmentKey}`;
        const snapshot = await get(ref(this.db, attachmentPath));
        if (!snapshot.exists()) throw new Error('Bilagan hittades inte');
        await update(ref(this.db, attachmentPath), { displayName: displayName ?? null });
        recordActivity('document.updated', { targetKey: workOrderKey });
    }

    /**
     * Get download URL for an attachment
     */
    async getAttachmentDownloadUrl(storagePath: string): Promise<string> {
        return await getWorkOrderAttachmentDownloadUrl(storagePath);
    }
}

// Export singleton instance
export const workOrdersApi = new WorkOrdersApi();
